import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Container } from "react-bootstrap";
import { toast } from "react-toastify";

const announcements = [
  {
    id: 1,
    icon: "ri-truck-line",
    badge: "DELIVERY",
    text: "Complimentary White-Glove Delivery Across Egypt on Orders Over $150",
    linkText: "Shop Collection",
    linkUrl: "/shop",
  },
  {
    id: 2,
    icon: "ri-coupon-3-line",
    badge: "EXCLUSIVE OFFER",
    text: "Use Code for 10% Off Your First Atelier Order:",
    code: "ATELIER10",
  },
  {
    id: 3,
    icon: "ri-sparkling-2-line",
    badge: "CURATED",
    text: "Architectural Living: Handcrafted Solid Oak & Premium Bouclé Pieces",
    linkText: "View Lookbook",
    linkUrl: "/lookbook",
  },
  {
    id: 4,
    icon: "ri-shield-check-line",
    badge: "GUARANTEE",
    text: "Official 5-Year Structural Warranty & 30-Day In-Home Comfort Trial",
    linkText: "Our Atelier",
    linkUrl: "/about",
  },
];

const AnnouncementBar = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [copied, setCopied] = useState(false);

  // Auto-advance every 4.2 seconds unless hovered
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % announcements.length);
    }, 4200);

    return () => clearInterval(interval);
  }, [isPaused]);

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % announcements.length);
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + announcements.length) % announcements.length);
  };

  const handleCopyCode = (code, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success(`Promo code "${code}" copied to clipboard!`, {
      autoClose: 2000,
    });
    setTimeout(() => setCopied(false), 2500);
  };

  const current = announcements[currentIndex];

  return (
    <div
      className="announcement__bar"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      role="region"
      aria-label="Atelier Announcements"
    >
      <Container>
        <div className="announcement__wrapper">
          {/* Left Live Indicator */}
          <div className="announcement__live-tag d-none d-lg-flex">
            <span className="live__dot"></span>
            <span className="live__label">ATELIER LIVING</span>
          </div>

          {/* Center Dynamic Animated Carousel */}
          <div className="announcement__center">
            {/* Prev Button */}
            <button
              type="button"
              className="announcement__nav-btn"
              onClick={handlePrev}
              aria-label="Previous announcement"
              title="Previous offer"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>

            {/* Animated Announcement Content */}
            <div className="announcement__message-box">
              <AnimatePresence mode="wait">
                <motion.div
                  key={current.id}
                  className="announcement__message"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.28, ease: "easeOut" }}
                >
                  <i className={`${current.icon} announcement__icon`}></i>
                  
                  <span className="announcement__badge">{current.badge}</span>
                  
                  <span className="announcement__text">{current.text}</span>

                  {/* Optional Copyable Code */}
                  {current.code && (
                    <button
                      type="button"
                      className={`announcement__code-btn ${copied ? "copied" : ""}`}
                      onClick={(e) => handleCopyCode(current.code, e)}
                      title="Click to copy promo code"
                    >
                      <span>{current.code}</span>
                      <i className={copied ? "ri-check-line" : "ri-file-copy-line"}></i>
                    </button>
                  )}

                  {/* Optional Action Link */}
                  {current.linkText && current.linkUrl && (
                    <Link to={current.linkUrl} className="announcement__link">
                      <span>{current.linkText}</span>
                      <i className="ri-arrow-right-s-line"></i>
                    </Link>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Next Button */}
            <button
              type="button"
              className="announcement__nav-btn"
              onClick={handleNext}
              aria-label="Next announcement"
              title="Next offer"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>

          {/* Right Utility Concierge & Currency */}
          <div className="announcement__right d-none d-md-flex">
            <Link to="/contact" className="announcement__util-link">
              <i className="ri-customer-service-2-line"></i>
              <span>Concierge</span>
            </Link>
            <span className="announcement__sep">&bull;</span>
            <span className="announcement__currency">EGP / USD</span>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default AnnouncementBar;
